import clone from 'lodash/cloneDeep'
import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isString from 'lodash/isString'
import uniq from 'lodash/uniq'
import flow from 'lodash/fp/flow'
import upperFirst from 'lodash/upperFirst'

/**
 * @param {object} product
 * @param {number|string} size
 * @param {boolean} [isGiftCard=false]
 */
export const getVariant = (product, size, isGiftCard = false) => {
  let result = null
  if (product.variants.length == 1) {
    result = product.variants[0]
  } else {
    for (let variant of product.variants || []) {
      const sizeObj = variant.selectedOptions.find(({ name }) => {
        return name === (isGiftCard ? "Title" : "Size")
      })
      if (sizeObj && sizeObj.value == size) {
        result = variant
        break
      }
    }
  }
  return clone(result)
}
/**
 * map line item from product and variant objects
 * @param {object} product
 * @param {object} variant
 */
export const getLineItem = (product, variant, { quantity, metafields, customAttributes } = {}) => ({
  variant,
  image: product.featuredMedia,
  title: product.title,
  quantity: quantity || 1,
  productId: product.id,
  pimSyncSourceProductId: product.pimSyncSourceProductId,
  handle: product.handle,
  vendor: product.vendor,
  tags: product.tags,
  metafields: clone(metafields || product.metafields || []),
  customAttributes: customAttributes || [],
  productType: product.productType,
})

export const getSizeOptions = (product) => {
  let sizes = []
  if (product && product.variants) {
    for (let variant of product.variants) {
      const size = variant.selectedOptions.find(({ name }) => name === 'Size')
      sizes.push({
        label: size && size.value,
        value: size && size.value,
        disabled: !variant.availableForSale,
        variantId: variant.id,
      })
    }
  }
  return uniq(sizes)
}

/**
 * determine if a product is "new" either by its age or if the "show_new_badge"
 * metafield is set to true.
 * @param {object} product
 * @return {boolean}
 */
export const productIsNew = (product) => {
  const now = parseInt(Date.now() / 1000)
  const createdAt = get(product, 'createdAt')
  const productTags = get(product, 'tags')
  const age = (now - createdAt) / 86400
  let result = false
  if ((age <= 45) || productTags.includes('new-arrival')) {
    result = true
  }
  // console.log('[new]', result, product.handle, productTags)
  return result
}
/**
 * maintain shopify order. place "new" products at the front of the list.
 * @param {object[]} products - array of product objects
 * @return {object[]} sorted products
 */
export const sortProducts = (products) => {
  let newProducts = []
  let rest = []
  for (let product of products) {
    if (productIsNew(product)) {
      newProducts.push(product)
    } else {
      rest.push(product)
    }
  }
  return newProducts.concat(rest)
}
const isJson = (v) => {
  const lastIndex = v.length - 1
  return (
    isString(v) &&
    (
      ((v[0] === '{') && (v[lastIndex] === '}')) ||
      ((v[0] === '[') && (v[lastIndex] === ']'))
    )
  )
}

const sectionReducer = function (sections, section, index) {
  const { mapSection } = require('./contentfulHelper')
  const vm = this
  if (index > 0 && section.tags.includes('childSection')) {
    const parent = sections[sections.length - 1]
    const child = mapSection(vm, section)
    if (parent.children) {
      parent.children.push(child)
    } else {
      parent.children = [child]
    }
  } else {
    section.fields = { ...section }
    sections.push(section)
  }
  return sections
}

/**
 * map shopify section data to a format that is accepted by Pa eContent and
 * related content type components
 * @param {object} vm - vue instance
 * @param {object[]} sections - shopify content sections
 */
export const reduceShopifySections = (vm, sections) => {
  const { mapSections } = require('./contentfulHelper')
  const reducedSections = sections.reduce(sectionReducer.bind(vm), [])
  return mapSections(vm, reducedSections)
}

/**
 * produce a map of the metafields for the given object
 * used to access metafields easily by key: `product.metafieldsMap[key]`
 * @param {{metafields: object[]}} - productOrVariant
 * @return {object}
 */
export const mapMetafields = (productOrVariant) => {
  const item = productOrVariant
  let metafieldsMap = {}
  if (isArray(item.metafields)) {
    const stringArrayRegex = /(?:[^|]\|[^|])+/
    for (let field of clone(item.metafields)) {
      if (isJson(field.value)) {
        // parse JSON
        metafieldsMap[field.key] = {
          ...field,
          value: JSON.parse(field.value)
        }
      } else if (
        isString(field.value) &&
        stringArrayRegex.test(field.value)
      ) {
        // parse these string array things
        // <value>|<value>|<value> -> [<value>,<value>,<value>]
        metafieldsMap[field.key] = {
          ...field,
          value: field.value.split('|')
        }
      } else {
        metafieldsMap[field.key] = field
      }
    }
  }
  return metafieldsMap
}
/**
 * @param {object} product - shopify product object
 */
const mapFacets = (product) => {
  const facetDenylist = [ 'color' ]
  const tags = product.tags || []
  const variants = product.variants || []
  // Get product filter facets from variant data
  const variantOptions = variants.map(variant => {
    return variant.selectedOptions
  })
  const variantFacets = variantOptions
    .reduce((acc, item) => {
      return acc.concat(item)
    }, [])
    .map(option => JSON.stringify(option))
  let facets = []
  for (let item of Array.from(new Set(variantFacets))) {
    const option = JSON.parse(item)
    const name = option.name.toLowerCase()
    if (!facetDenylist.includes(name)) {
      facets.push({ name, value: option.value })
    }
  }
  // Get product filter facets from tags. Tags should be formatted
  // "filter_property-name_value"
  for (let facet of tags) {
    if (facet.includes('filter')) {
      const facetFragments = facet.split('_')
      const facetName = facetFragments[1]
      const facetValue = () => {
        const fragments = facetFragments[2].split('-')
        return fragments
          .map(fragment => {
            return `${fragment
              .charAt(0)
              .toUpperCase()}${fragment.substring(1)}`
          })
          .join(' ')
      }
      product[facetName] = facetValue()
      facets.push({ name: facetName, value: facetValue() })
    } else if (facet.includes('gender:')) {
      const [ name, value ] = facet.split(':')
      facets.push({ name , value })
    }
  }
  if (product.productType) {
    facets.push({ name: 'productType', value: product.productType })
  }
  if (
    product.metafieldsMap &&
    product.metafieldsMap.engraving &&
    product.metafieldsMap.engraving.value
  ) {
    // if engraving metafield is valid, flag the facet
    if (
      product.metafieldsMap.engraving.value.font &&
      product.metafieldsMap.engraving.value.font.length &&
      product.metafieldsMap.engraving.value.location &&
      product.metafieldsMap.engraving.value.location.length
    ) {
      facets.push({ name: 'engraving', value: 'Yes' })
    } else {
      console.warn('[shopifyHelper/facets] invalid engraving data', product.metafieldsMap.engraving)
      facets.push({ name: 'engraving', value: '?' })
    }
  } else {
    facets.push({ name: 'engraving', value: 'No' })
  }
  return (facets.length && variants.length)
    ? { ...product, variantOptions, facets, minPrice: get(product, 'priceRange.min') }
    : product
}
/**
 * standard mapping from shopify image data to our internal format
 * @param {object} - product
 * @return {object[]}
 */
const mapProductImages = (product) => {
  let media = []
  let hoverImage
  if (isArray(product.media)) {
    for (let item of product.media) {
      const { id, src, thumbnailSrc, type } = clone(item)
      const image = {
        alt: item.altText || item.alt,
        id,
        src,
        thumbnailSrc,
        type
      }
      media.push(image)
    }
    for (let image of media) {
      if (
        image.alt &&
        typeof image.alt === "string" &&
        image.alt.toLowerCase().includes('hover')
      ) {
        // if there is an alt tag with hover than use that
        hoverImage = image
        break
      } else if (
        image.alt &&
        typeof image.alt === "string" &&
        image.alt.toLowerCase().includes('lifestyle')
      ) {
        // if you never find an image with hover in the alt than you
        // can use one with lifestyle. This is old and will deprecate
        hoverImage = image
      }
    }
    if (!hoverImage) {
      // if you don't find an image with hover or lifestyle than set the
      // hover image to the first image which should always be the 3/4 render image
      hoverImage = media[0] || {}
    }
  }
  return {
    ...product,
    media,
    featuredMedia: media[0] || {},
    featuredImage: media[0] || {},
    hoverMedia: hoverImage,
    hoverImage,
  }
}
const mapVariants = (product) => {
  let variants = []
  if (isArray(product.variants)) {
    const bundleComparePrice = get(product, 'metafieldsMap.bundleComparePrice.value')
    for (let variant of product.variants) {
      variants.push({
        ...variant,
        metafieldsMap: mapMetafields(variant),
        compareAtPrice: bundleComparePrice || variant.compareAtPrice
      })
    }
  }
  return { ...product, variants }
}
/**
 * map a single product's fields to our internal format
 */
export const mapProduct = flow(
  /**
   * normalize raw product data before applying mapping functions
   */
  (rawProduct) => {
    const product = clone(rawProduct)
    const bundleComparePrice = get(product, 'metafieldsMap.bundleComparePrice.value')
    return {
      ...product,
      productType: (product.productType)
        ? upperFirst(product.productType)
        : product.productType,
      compareAtPrice: bundleComparePrice || product.compareAtPrice
    }
  },
  /**
   * map metafields
   */
  (product) => clone({
    ...product,
    metafieldsMap: mapMetafields(product),
    compareAtPrice: get(product, 'metafieldsMap.bundleComparePrice.value') || product.compareAtPrice
  }),
  mapFacets,
  mapProductImages,
  mapVariants,
)

/**
 * map shopify objects to our internal product object schema
 * @param {object[]} products
 */
export const mapProducts = (products) => {
  let result = []
  for (let product of products) {
    result.push(mapProduct(product))
  }
  return result
}
